import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type {
  UpdateProjectAiVersionRequest,
  UpdateProjectContextRequest,
} from '@ai-meeting/shared'
import { useUiStrings } from '@/hooks/useUiStrings'
import { useProjectContext } from '../useProjectContext'
import { useProjectContextVersions } from '../useProjectContextVersions'
import {
  canRestoreProjectContextVersion,
  type ProjectContextVersion,
} from '../projectContextUi'
import ProjectContextCard from './ProjectContextCard'
import ProjectContextVersionsCard from './ProjectContextVersionsCard'
import ProjectDictionaryPanel from './ProjectDictionaryPanel'

type ContextSubTab = 'context' | 'dictionary'

function subTabClass(active: boolean): string {
  return [
    'rounded-lg px-4 py-1.5 text-xs transition-colors',
    active
      ? 'bg-[var(--bg-secondary)] font-semibold text-[var(--text-primary)] shadow-sm'
      : 'font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
  ].join(' ')
}

type Props = {
  projectKey: string
}

/**
 * Context & Dictionary tab.
 *
 * The context sub-tab is the two cards the design gives it: the editable AI
 * version, and its version history. Saving an edit files a new `human` version;
 * any earlier AI version can be restored.
 */
export default function ProjectContextTab({ projectKey }: Props) {
  const { t } = useUiStrings()
  const navigate = useNavigate()
  const {
    state,
    context,
    errorMessage,
    saving,
    save,
    saveAiVersion,
    reload: reloadContext,
  } = useProjectContext(projectKey)
  const {
    state: versionsState,
    versions,
    errorMessage: versionsError,
    busy: versionsBusy,
    reload: reloadVersions,
    loadDetail,
    restore,
  } = useProjectContextVersions(projectKey)
  const [subTab, setSubTab] = useState<ContextSubTab>('context')

  const latestVersion = versions.find((version) => version.active) ?? null
  const busy = saving || versionsBusy

  /**
   * A human edit files a new version and moves the active pointer — refresh
   * the list so the badge and "In use" row match.
   */
  async function handleSaveAiVersion(body: UpdateProjectAiVersionRequest) {
    const result = await saveAiVersion(body)
    await reloadVersions()
    return result
  }

  /** Dictionary saves the declared half; it no longer files context versions. */
  async function handleSaveContext(body: UpdateProjectContextRequest) {
    return save(body)
  }

  async function handleRestore(version: ProjectContextVersion) {
    if (!canRestoreProjectContextVersion(version)) return
    try {
      await restore(version)
      await reloadContext()
    } catch {
      // Hook already surfaces the error message.
    }
  }

  function handleOpenMeeting(version: ProjectContextVersion) {
    if (!version.meetingId) return
    navigate(`/meetings/${version.meetingId}`)
  }

  return (
    <div className="flex flex-col gap-6" data-testid="project-context-tab">
      <div
        role="tablist"
        aria-label={t.projectDetailContextTab}
        className="inline-flex w-fit items-center rounded-xl bg-[var(--bg-tertiary)] p-1"
      >
        <button
          type="button"
          role="tab"
          aria-selected={subTab === 'context'}
          onClick={() => setSubTab('context')}
          className={subTabClass(subTab === 'context')}
        >
          {t.projectDetailContextSubtab}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={subTab === 'dictionary'}
          onClick={() => setSubTab('dictionary')}
          className={subTabClass(subTab === 'dictionary')}
        >
          {t.projectDetailDictionarySubtab}
        </button>
      </div>

      {state === 'loading' && (
        <p className="text-sm text-[var(--text-secondary)]">{t.loading}</p>
      )}

      {state === 'error' && (
        <p role="alert" className="text-sm text-[var(--error)]">
          {errorMessage ?? t.failedLoadProjectContext}
        </p>
      )}

      {state === 'ready' && context && subTab === 'context' && (
        <>
          <ProjectContextCard
            context={context}
            saving={busy}
            errorMessage={errorMessage ?? versionsError}
            onSave={handleSaveAiVersion}
            latestVersion={latestVersion}
          />
          {versionsState === 'error' && versionsError && (
            <p role="alert" className="text-sm text-[var(--error)]">
              {versionsError}
            </p>
          )}
          <ProjectContextVersionsCard
            versions={versions}
            busy={busy}
            onLoadVersionDetail={loadDetail}
            onRestoreVersion={(version) => void handleRestore(version)}
            onOpenMeeting={handleOpenMeeting}
          />
        </>
      )}

      {state === 'ready' && context && subTab === 'dictionary' && (
        <ProjectDictionaryPanel
          context={context}
          saving={saving}
          errorMessage={errorMessage}
          onSave={handleSaveContext}
        />
      )}
    </div>
  )
}
